/**
 * Acquisition — how far new people got, and where they came from.
 *
 * The Funnel tab beside this counts visits; this counts PEOPLE, which is the
 * question a campaign asks. The science site makes a guest on the first page
 * view, so "had an account" is true of nearly everyone there and says nothing
 * about signing up — this tab reads whether that account stopped being a guest.
 *
 * Two funnels, because the two properties record arrivals differently: the
 * science site by the host that referred a person's first page, practice links
 * by the link and the placement it was followed from. The definitions live in the
 * backend (`admin/analytics/acquisitionFunnel.ts`); the notes here say them in
 * a sentence each.
 */
import { useEffect, useState } from 'react';
import { InfoTip } from '@shared-ui/components';
import { firstTouchLabel, referrerKindLabel, siteLabel } from '../shared/sourceLabels';
import { acquisitionFunnelApi, type AcquisitionFunnel, type ScienceSteps } from '../../../api/siteAnalyticsApi';


const STEPS: { key: keyof ScienceSteps; label: string; title: string }[] = [
  { key: 'arrived', label: 'Arrived', title: 'New people: their first account was made in this window' },
  { key: 'openedSimulation', label: 'Simulation', title: 'Opened any simulation' },
  { key: 'openedTwoSimulations', label: '2+ simulations', title: 'Opened two different simulations' },
  { key: 'usedLab', label: 'Lab', title: 'Used the chemistry lab (mixed at least once)' },
  { key: 'signedUp', label: 'Signed up', title: 'An account of theirs is no longer a guest' },
  { key: 'cameBack', label: 'Came back', title: 'Active on two or more different days (UTC)' },
];

const CONVERSION_STATUSES = ['sent', 'pending', 'failed', 'expired', 'blocked'] as const;
const CONVERSION_LABELS: Record<string, string> = {
  SecondSimulation: 'Opened a second simulation',
  CompleteRegistration: 'Signed up',
};

/** A count with its share of the row's arrivals — the share is what compares
 *  across sources of very different sizes. */

function Cell({ n, of }: { n: number; of: number }) {
  return (
    <td className="sa-num">
      <span className="acq-n">
        {n.toLocaleString()}
        {of > 0 && n !== of && <span className="acq-pct">{Math.round((n / of) * 100)}%</span>}
      </span>
    </td>
  );
}

export default function SiteAcquisition({ from, to }: { from: Date; to: Date }) {
  // Keyed by the window it answers, so a stale answer is never shown for a new
  // window and nothing has to be reset inside the effect.
  const key = `${from.toISOString()}|${to.toISOString()}`;
  const [result, setResult] = useState<{ key: string; data?: AcquisitionFunnel; error?: string } | null>(null);

  useEffect(() => {
    let live = true;
    acquisitionFunnelApi.get(from, to)
      .then(data => { if (live) setResult({ key, data }); })
      .catch(e => { if (live) setResult({ key, error: e instanceof Error ? e.message : 'Could not load the funnel.' }); });
    return () => { live = false; };
  }, [from, to, key]);

  if (!result || result.key !== key) return <p className="sa-empty">Loading…</p>;
  if (result.error || !result.data) return <p className="sa-error">{result.error}</p>;
  const data = result.data;

  const { science, links, conversions, campaigns, sources } = data;
  const conversionEvents = [...new Set(conversions.map(c => c.event))].sort();
  const count = (event: string, status: string) =>
    conversions.find(c => c.event === event && c.status === status)?.n ?? 0;

  return (
    <>
      <section className="sa-panel">
        <h2>
          Science site — new readers, by the site that referred them
          <InfoTip>
            <span className="sa-tip-p">
              Counted in people. A guest and the account they sign up with in the same session are one
              person. The source is the site that referred their first page; “No referrer” includes
              typed URLs and most in-app browsers (Instagram, Facebook), so an ad cannot yet be told
              apart from other traffic. Percentages are of that row’s arrivals.
            </span>
            <span className="sa-tip-p">
              Not in the table: {science.returningPeople.toLocaleString()} returning{' '}
              {science.returningPeople === 1 ? 'person' : 'people'} (first seen before this window),{' '}
              {science.excludedPeople.toLocaleString()} test or dummy{' '}
              {science.excludedPeople === 1 ? 'account' : 'accounts'}, and{' '}
              {science.unidentifiedVisits.toLocaleString()}{' '}
              {science.unidentifiedVisits === 1 ? 'session' : 'sessions'} with no account at all (storage
              refused, or the guest could not be made).
            </span>
          </InfoTip>
        </h2>
        {science.total.arrived === 0
          ? <p className="sa-empty">No new people in this window.</p>
          : (
            <div className="acq-scroll">
              <table className="sa-table acq-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    {STEPS.map(s => <th key={s.key} className="sa-num" title={s.title}>{s.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {science.bySource.map(r => (
                    <tr key={r.source}>
                      <td className="sa-label">{referrerKindLabel(r.source)}</td>
                      {STEPS.map(s => <Cell key={s.key} n={r[s.key]} of={r.arrived} />)}
                    </tr>
                  ))}
                  <tr className="acq-total">
                    <td className="sa-label">All sources</td>
                    {STEPS.map(s => <Cell key={s.key} n={science.total[s.key]} of={science.total.arrived} />)}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
      </section>

      <section className="sa-panel">
        <h2>
          New accounts on both sites — by first touch
          <InfoTip>
            <span className="sa-tip-p">
              Every account created in this window, by its first touch — what the page it landed on knew,
              written once at creation. Counted in accounts, not sessions or people. Most are guests: the
              science site makes one for every new reader, so on the science site new accounts are close to
              new readers, counted by a different rule from the table above. First touch, which the ad
              platforms (last touch) will disagree with. “Not recorded” is an account from before capture
              began, never a guess.
            </span>
            <span className="sa-tip-p">
              Answered is practice in the BuzzBrain app. A science-site reader rarely does it, so their rows
              show “—” where nobody did, and the count where somebody went on to.
            </span>
          </InfoTip>
        </h2>
        {sources.length === 0
          ? <p className="sa-empty">No accounts created in this window.</p>
          : (
            <div className="acq-scroll">
              <table className="sa-table acq-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Site</th>
                    <th className="sa-num" title="Accounts created in the window with this first touch">New accounts</th>
                    <th className="sa-num" title="Of those, still a guest — made for a reader or a link follower who has not signed up">Guests</th>
                    <th className="sa-num" title="Of those, not (or no longer) a guest — signed up, or made by a teacher or admin">Signed up</th>
                    <th className="sa-num" title="Of those, opened at least one simulation — on the science site or in the app">Opened a simulation</th>
                    <th className="sa-num" title="Of those, answered at least one question in the BuzzBrain app. — on a science-site row where nobody did">Answered</th>
                    <th className="sa-num" title="Of those, active on a later day than they were created">Came back</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map(r => (
                    <tr key={`${r.source}|${r.property}`}>
                      <td className="sa-label">{firstTouchLabel(r.source)}</td>
                      <td>{siteLabel(r.property)}</td>
                      <Cell n={r.accounts} of={r.accounts} />
                      <Cell n={r.accounts - r.signedUp} of={r.accounts} />
                      <Cell n={r.signedUp} of={r.accounts} />
                      {r.openedSimulation === undefined
                        ? <td className="sa-num sa-zero">—</td>
                        : <Cell n={r.openedSimulation} of={r.accounts} />}
                      {/* Practice is the app's: a science-site row where nobody
                          answered reads "—", not a 0 that looks like a result. */}
                      {r.property === 'zitmus' && r.answered === 0
                        ? <td className="sa-num sa-zero">—</td>
                        : <Cell n={r.answered} of={r.accounts} />}
                      <Cell n={r.cameBack} of={r.accounts} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>

      <section className="sa-panel">
        <h2>
          Practice links — by link and placement
          <InfoTip>
            <span className="sa-tip-p">
              “Opened” is browsers that loaded the link; the other columns are the guests the link made in
              this window. Someone who already had an account opens a link without becoming its guest, so
              “Became guest” can be lower for a reason other than drop-off.
            </span>
          </InfoTip>
        </h2>
        {links.length === 0
          ? <p className="sa-empty">No practice-link opens or arrivals in this window.</p>
          : (
            <div className="acq-scroll">
              <table className="sa-table acq-table">
                <thead>
                  <tr>
                    <th>Link</th>
                    <th>Channel</th>
                    <th className="sa-num" title="Distinct browsers that loaded the link">Opened</th>
                    <th className="sa-num" title="Guests created with this link and placement">Became guest</th>
                    <th className="sa-num" title="Of those guests, answered at least one question">Answered</th>
                    <th className="sa-num" title="Of those guests, no longer a guest">Signed up</th>
                    <th className="sa-num" title="Of those guests, active on a later day">Came back</th>
                  </tr>
                </thead>
                <tbody>
                  {links.map(l => (
                    <tr key={`${l.slug}|${l.placement}`}>
                      <td className="sa-label">/p/{l.slug}</td>
                      <td>{l.placement}</td>
                      <td className="sa-num">{l.opened.toLocaleString()}</td>
                      <Cell n={l.becameGuest} of={l.becameGuest} />
                      <Cell n={l.answered} of={l.becameGuest} />
                      <Cell n={l.signedUp} of={l.becameGuest} />
                      <Cell n={l.cameBack} of={l.becameGuest} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>

      <section className="sa-panel">
        <h2>
          Meta ad campaigns
          <InfoTip>
            <span className="sa-tip-p">
              Every campaign and ad running at once, side by side, by the tags on the ad’s URL. Clicks are
              ad clicks that landed on the science site; the other columns are people from those clicks
              who went on to do it. Meta credits each conversion to the right ad itself — this table is
              our own view. An ad whose URL was not tagged shows as <em>Untagged</em>: add the URL
              parameters from Settings → Ad Campaigns to it.
            </span>
          </InfoTip>
        </h2>
        {campaigns.length === 0
          ? <p className="sa-empty">No ad clicks in this window.</p>
          : (
            <div className="acq-scroll">
              <table className="sa-table acq-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Ad</th>
                    <th className="sa-num" title="Ad clicks that landed on the science site">Clicks</th>
                    <th className="sa-num" title="Opened a second, different simulation">2+ simulations</th>
                    <th className="sa-num" title="Signed up">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => (
                    <tr key={`${c.campaign ?? ''}|${c.content ?? ''}`}>
                      <td className="sa-label">{c.campaign ?? <em>Untagged</em>}</td>
                      <td>{c.content ?? '—'}</td>
                      <td className="sa-num">{c.clicks.toLocaleString()}</td>
                      <Cell n={c.secondSimulation} of={c.clicks} />
                      <Cell n={c.signedUp} of={c.clicks} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>

      <section className="sa-panel">
        <h2>
          Conversions reported to Meta
          <InfoTip>
            <span className="sa-tip-p">
              Only people who landed from a Meta ad click appear here. <strong>Blocked</strong> are
              recorded and never sent — accounts from the student app, a school, or a partner’s link.
              <strong> Pending</strong> are waiting for the next send (or for sending to be switched on);
              <strong> expired</strong> were older than Meta accepts by then.
            </span>
          </InfoTip>
        </h2>
        {conversionEvents.length === 0
          ? <p className="sa-empty">No conversions in this window.</p>
          : (
            <div className="acq-scroll">
              <table className="sa-table acq-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    {CONVERSION_STATUSES.map(s => <th key={s} className="sa-num">{s[0].toUpperCase() + s.slice(1)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {conversionEvents.map(e => (
                    <tr key={e}>
                      <td className="sa-label">{CONVERSION_LABELS[e] ?? e}</td>
                      {CONVERSION_STATUSES.map(s => <td key={s} className="sa-num">{count(e, s).toLocaleString()}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>
    </>
  );
}
